// src/App.js

import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { ThemeProvider } from '@mui/material/styles';
import store from './store';
import theme from './theme';

import ProfilePage from './components/ProfilePage';
import HomePage from './components/HomePage';
import RegistrationForm from './components/SignUp';
import LoginPage from './components/SignInSide';
import GatedRoute from './components/GatedRoute';
import TranslationDashboard from './components/TranslationDashboard';
import EditProject from './components/EditProject';
import TranslateProject from './components/TranslateProject';
import ProjectDetails from './components/ProjectDetails';
import TranslationProjects from './components/TranslationProjects';
import NewTranslation from './components/NewTranslation';
import LanguageSettings from './components/LanguageSettings';
import AccountSettings from './components/AccountSettings';
import DashboardHome from './components/DashboardHome';
import { loginSuccess, logout, authCheckCompleted } from './slices/authSlice';
import { clearAuthToken, getStoredToken, getStoredTokenData, storeAuthToken } from './utils/authToken';
import Team from './components/TeamPage';
import GlossaryTerms from './components/GlossaryTerms';
import AdminUsersPage from './components/AdminUsersPage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import AdminDeepLUsagePage from './components/AdminDeepLUsagePage';
import AdminAIUsagePage from './components/AdminAIUsagePage';
import AdminSystemLogsPage from './components/AdminSystemLogsPage';
import UtilitiesPage from './components/UtilitiesPage';
import ChatPage from './components/ChatPage';
import FileManager from './components/FileManager';
import TaskBoard from './components/TaskBoard';
import ResetPasswordPage from './components/ResetPasswordPage';
import AnalyticsPage from './components/AnalyticsPage';
import AppsMarketplace from './components/AppsMarketplace';
import SupportCenter from './components/SupportCenter';
import AdminSupportManager from './components/AdminSupportManager';
import AdminSupportTickets from './components/AdminSupportTickets';
import JobsBoard from './components/JobsBoard';
import Pricing from './components/Pricing';
import AboutPageWrapper from './components/AboutPageWrapper';
import ContactPageWrapper from './components/ContactPageWrapper';
import FAQPageWrapper from './components/FAQPageWrapper';

import axios from 'axios';

// Protected route for logged-in users
const ProtectedRoute = ({ element: Component }) => {
    const { isAuthenticated, isLoading } = useSelector((state) => state.auth);
    const location = useLocation();

    if (isLoading) {
        return null;
    }

    return isAuthenticated ? (
        <Component />
    ) : (
        <Navigate to="/login" replace state={{ from: location }} />
    );
};

const App = () => {
    const dispatch = useDispatch();
    const { token, user } = useSelector((state) => state.auth);
    const [socket, setSocket] = useState(null);

    // Restore session
    useEffect(() => {
        const savedToken = getStoredToken();
        if (!savedToken) {
            dispatch(authCheckCompleted());
            return;
        }

        axios
            .get('/api/users/me', {
                headers: { Authorization: `Bearer ${savedToken}` },
            })
            .then((res) => {
                dispatch(loginSuccess({ token: savedToken, user: res.data }));
            })
            .catch(() => {
                clearAuthToken();
                dispatch(logout());
            });
    }, [dispatch]);

    // Socket for presence
    useEffect(() => {
        if (!token || !user) return;
        const existing = getStoredTokenData();
        if (!existing || existing.token !== token) {
            storeAuthToken(token, existing?.rememberMe ?? true);
        }
        const sock = io('/', { transports: ['websocket', 'polling'] });
        sock.emit('join', user._id);
        sock.on('statusUpdate', ({ userId, status }) => {
            // handle if needed
        });
        setSocket(sock);
        return () => {
            sock.disconnect();
            setSocket(null);
        };
    }, [token, user]);

    return (
        <ThemeProvider theme={theme}>
            <Router>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPageWrapper />} />
                    <Route path="/contact" element={<ContactPageWrapper />} />
                    <Route path="/faqs" element={<FAQPageWrapper />} />
                    <Route path="/register" element={<RegistrationForm />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Protected dashboard and nested */}
                    <Route
                        path="/dashboard/*"
                        element={<ProtectedRoute element={TranslationDashboard} />}
                    >
                        <Route path="" element={<DashboardHome />} />
                        <Route path="projects" element={<TranslationProjects />} />
                        <Route path="new-translation" element={<NewTranslation />} />
                        <Route path="projects/:id/edit" element={<EditProject />} />
                        <Route path="projects/:id" element={<ProjectDetails />} />
                        <Route path="projects/:id/translate" element={<TranslateProject />} />
                        <Route path="projects/:id/tasks" element={<TaskBoard />} />
                        <Route path="language-settings" element={<LanguageSettings />} />
                        <Route path="account-settings" element={<AccountSettings />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route
                            path="team"
                            element={<GatedRoute element={Team} requiredFeature="teamAccess" />}
                        />
                        <Route path="apps" element={<AppsMarketplace />} />
                        <Route path="support" element={<SupportCenter />} />
                        <Route path="support/:articleSlug" element={<SupportCenter />} />
                        <Route
                            path="chat/:userId?"
                            element={<GatedRoute element={() => <ChatPage socket={window.__SOCKET__} />} requiredFeature="chatAccess" />}
                        />
                        <Route path="file-manager" element={<FileManager />} />
                        <Route path="jobs" element={<JobsBoard />} />
                        <Route
                            path="analytics"
                            element={<GatedRoute element={AnalyticsPage} requiredFeature="analytics" />}
                        />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="glossary/:glossaryId/terms" element={<GlossaryTerms />} />
                        <Route
                            path="admin/deepl-usage"
                            element={<ProtectedAdminRoute element={AdminDeepLUsagePage} />}
                        />
                        <Route
                            path="admin/logs"
                            element={<ProtectedAdminRoute element={AdminSystemLogsPage} />}
                        />
                        <Route
                            path="admin/ai-usage"
                            element={<ProtectedAdminRoute element={AdminAIUsagePage} />}
                        />
                        <Route
                            path="admin/utilities"
                            element={<ProtectedAdminRoute element={UtilitiesPage} />}
                        />
                        <Route
                            path="admin/support"
                            element={<ProtectedAdminRoute element={AdminSupportManager} />}
                        />
                        <Route
                            path="admin/support-tickets"
                            element={<ProtectedAdminRoute element={AdminSupportTickets} />}
                        />
                        <Route
                            path="admin/users"
                            element={<ProtectedAdminRoute element={AdminUsersPage} />}
                        />
                    </Route>
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

// Wrap App in Redux Provider
const RootApp = () => (
    <Provider store={store}>
        <App />
    </Provider>
);

export default RootApp;
